import { COLORS } from "../styles/tokens";

/**
 * GlobalStyles — injects fonts, animations, and base resets.
 *
 * Lives at the root of the app so all components can rely on:
 *   - .display     Outfit display font
 *   - .mono        JetBrains Mono
 *   - .glass       Frosted-glass surface
 *   - .grid-bg     Faint navy grid (matches LID design system)
 *   - .pulse-dot   Live indicator dot
 *   - .stream-line Animated horizontal data stream
 *   - .aurora-1/2  Drifting radial light blobs
 *   - .badge-float Subtle vertical drift for the badge logo
 *   - .fade-up / .slide-up / .slide-right  Entry animations
 *   - .shimmer-border  Hover shimmer for cards
 *   - .noise-overlay   Film-grain texture
 *
 * All animations honor reduced-motion preferences.
 */
export function GlobalStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');

      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; -webkit-tap-highlight-color: transparent; }
      body { overflow-x: hidden; }

      .display { font-family: 'Outfit', sans-serif; letter-spacing: -0.02em; }
      .mono { font-family: 'JetBrains Mono', monospace; }

      .layer { position: relative; z-index: 1; }

      .glass {
        background: rgba(15, 29, 51, 0.75);
        backdrop-filter: blur(16px) saturate(140%);
        -webkit-backdrop-filter: blur(16px) saturate(140%);
      }

      .grid-bg {
        background-image:
          linear-gradient(rgba(59, 130, 246, 0.06) 1px, transparent 1px),
          linear-gradient(90deg, rgba(59, 130, 246, 0.06) 1px, transparent 1px);
        background-size: 56px 56px;
        background-position: -1px -1px;
      }

      .pulse-dot {
        width: 6px; height: 6px; border-radius: 50%;
        background: ${COLORS.cyan};
        box-shadow: 0 0 12px ${COLORS.cyan};
        animation: pulse 2s ease-in-out infinite;
      }
      @keyframes pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.5; transform: scale(1.3); }
      }

      @keyframes streamLine {
        0% { transform: translateX(-100%); opacity: 0; }
        50% { opacity: 1; }
        100% { transform: translateX(100%); opacity: 0; }
      }
      .stream-line {
        position: absolute;
        height: 1px;
        background: linear-gradient(90deg, transparent, ${COLORS.cyan}, transparent);
        animation: streamLine 4s linear infinite;
      }

      @keyframes badgeFloat {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-3px); }
      }
      .badge-float { animation: badgeFloat 4s ease-in-out infinite; }

      @keyframes auroraDrift {
        0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.5; }
        33% { transform: translate(8%, -4%) scale(1.05); opacity: 0.7; }
        66% { transform: translate(-6%, 6%) scale(0.95); opacity: 0.6; }
      }
      .aurora-1 {
        position: absolute;
        width: 70vw;
        height: 70vw;
        max-width: 900px;
        max-height: 900px;
        top: -20%;
        left: -10%;
        background: radial-gradient(circle, ${COLORS.cyan}25, transparent 60%);
        filter: blur(40px);
        animation: auroraDrift 18s ease-in-out infinite;
        pointer-events: none;
      }
      .aurora-2 {
        position: absolute;
        width: 60vw;
        height: 60vw;
        max-width: 700px;
        max-height: 700px;
        bottom: -20%;
        right: -10%;
        background: radial-gradient(circle, ${COLORS.blue}30, transparent 60%);
        filter: blur(40px);
        animation: auroraDrift 22s ease-in-out infinite reverse;
        pointer-events: none;
      }

      /* Card elevation is state-driven (transform + multi-layer shadow set inline);
         this just smooths every depth transition. */
      .pkg-card {
        transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1),
                    box-shadow 0.35s cubic-bezier(0.16, 1, 0.3, 1),
                    border-color 0.25s ease;
      }

      .gradient-text {
        background: linear-gradient(135deg, ${COLORS.ink} 0%, ${COLORS.cyan} 100%);
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
      }

      @keyframes fadeUp {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .fade-up { animation: fadeUp 0.5s ease-out both; }

      @keyframes slideUp {
        from { transform: translateY(100%); }
        to { transform: translateY(0); }
      }
      .slide-up { animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1); }

      @keyframes slideRight {
        from { transform: translateX(100%); }
        to { transform: translateX(0); }
      }
      .slide-right { animation: slideRight 0.35s cubic-bezier(0.16, 1, 0.3, 1); }

      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
      .shimmer-border { position: relative; }
      .shimmer-border::before {
        content: "";
        position: absolute;
        inset: -1px;
        border-radius: inherit;
        padding: 1px;
        background: linear-gradient(90deg, transparent 0%, ${COLORS.cyan}50 50%, transparent 100%);
        background-size: 200% 100%;
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        animation: shimmer 3s linear infinite;
        opacity: 0;
        transition: opacity 0.3s;
        pointer-events: none;
      }
      .shimmer-border:hover::before { opacity: 1; }

      .noise-overlay {
        position: fixed;
        inset: 0;
        z-index: 2;
        pointer-events: none;
        opacity: 0.04;
        mix-blend-mode: overlay;
      }

      input, select, textarea {
        font-family: inherit;
        background: rgba(10, 19, 32, 0.6);
        color: ${COLORS.ink};
        border: 1px solid ${COLORS.border};
        outline: none;
        transition: border-color 0.2s, background 0.2s;
        -webkit-appearance: none;
        appearance: none;
      }
      input:focus, select:focus, textarea:focus {
        border-color: ${COLORS.cyan};
        background: rgba(10, 19, 32, 0.85);
        box-shadow: 0 0 0 3px ${COLORS.cyan}20;
      }
      input::placeholder, textarea::placeholder { color: ${COLORS.inkMute}; }

      button { font-family: inherit; cursor: pointer; -webkit-tap-highlight-color: transparent; }

      /* Touch-target sizing (mobile) and reduced-motion respect live in
         src/styles/tokens.css, which is loaded globally via main.tsx. */

      .no-scrollbar::-webkit-scrollbar { display: none; }
      .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

      @keyframes skeletonPulse {
        0%, 100% { opacity: 0.45; }
        50% { opacity: 0.75; }
      }
      .skeleton { animation: skeletonPulse 1.5s ease-in-out infinite; }

      @keyframes spin { to { transform: rotate(360deg); } }
      .spin { animation: spin 0.8s linear infinite; }
    `}</style>
  );
}
